/*
 * Copyright © 2026 Bruce Matin,  ALL RIGHTS RESERVED!
 */

import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ROUTES } from '../routes';
import { useTheme } from '../theme';
import { 
  ShoppingBag, 
  DollarSign, 
  TrendingUp, 
  PlusCircle, 
  Store, 
  ArrowRight,
  Search as SearchIcon,
  MessageSquare,
  Settings,
  Image as ImageIcon,
  Megaphone
} from 'lucide-react';
import { itemService } from '../services/itemService';

interface Item {
  id: number;
  title: string;
  description: string;
  price: number;
  status: string;
  createdAt: string;
  images?: string[];
}

interface Category {
  id: number;
  name: string;
}

interface DashboardData {
  summary: {
    totalItems: number;
    activeItems: number;
    totalSales: number;
    completedTransactions: number;
    estimatedProfit: number;
    profitMarginPercentage: number;
  };
  charts: {
    salesOverTime: Record<string, number>;
  };
  recentTransactions: any[];
  lastSearch?: {
    query: string;
    count: number;
  } | null;
}

const Dashboard: React.FC = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [myItems, setMyItems] = useState<Item[]>([]);
  const [searchResults, setSearchResults] = useState<Item[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [error, setError] = useState('');
  const [showAddItem, setShowAddItem] = useState(false);
  const [newItem, setNewItem] = useState({
    title: '',
    description: '',
    price: '',
    categoryId: '',
    location: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate(ROUTES.LOGIN);
        return;
      }

      try {
        const [dashRes, itemsRes] = await Promise.all([
          itemService.getDashboard(),
          itemService.getMyItems()
        ]);

        setDashboardData(dashRes.data.data);
        setMyItems(itemsRes.data.data);

        // Fetch categories for the add item form
        const categoriesRes = await itemService.getCategories();
        setCategories(categoriesRes.data.data);

        if (itemsRes.data.data.length === 0) {
          // If no items, fetch general search results as requested
          const searchRes = await itemService.listItems();
          setSearchResults(searchRes.data.data);
        }
      } catch (err: any) {
        if (err.response?.status === 401) {
          localStorage.removeItem('token');
          navigate(ROUTES.LOGIN);
        } else {
          // Only show error if we don't have basic dashboard structure
          // This prevents showing errors just because some data (like charts) might be missing
          console.error('Dashboard fetch error:', err);
          if (!dashboardData) {
            setError('Failed to load dashboard data.');
          }
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  const handleAddItem = async (e: React.FormEvent) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    try {
      await itemService.createItem(newItem);
      setShowAddItem(false);
      setNewItem({ title: '', description: '', price: '', categoryId: '', location: '' });
      // Refresh data
      window.location.reload();
    } catch (err) {
      setError('Failed to add item.');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: theme.colors.primary }}></div>
      </div>
    );
  }

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const hasItems = myItems.length > 0;
  // All plans should be able to sell items and set up a store.
  const hasStore = true;

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      {error && (
        <div className="border px-4 py-3 rounded-xl relative flex items-center justify-between" 
             style={{ backgroundColor: theme.colors.danger + '10', borderColor: theme.colors.danger, color: theme.colors.danger }}>
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-4 font-bold">×</button>
        </div>
      )}

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-black tracking-tight" style={{ color: theme.colors.text }}>Dashboard</h1>
            <p style={{ color: theme.colors.mutedText }}>Welcome back! Here's what's happening with your store.</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {!hasStore && (
              <Link
                to="/plans"
                className="inline-flex items-center px-6 py-2.5 rounded-xl font-bold transition-all hover:scale-[1.02]"
                style={{ backgroundColor: theme.colors.primary, color: '#fff' }}
              >
                <Store size={18} className="mr-2" />
                Open a Store
              </Link>
            )}
            {hasStore && (
              <>
                <Link
                  to={`/store/${localStorage.getItem('userId')}`}
                  className="inline-flex items-center px-5 py-2.5 border rounded-xl font-bold transition-all hover:opacity-80"
                  style={{ backgroundColor: theme.colors.surface, borderColor: theme.colors.border, color: theme.colors.text }}
                >
                  <Store size={18} className="mr-2" />
                  View Store
                </Link>
                <button
                  onClick={() => setShowAddItem(true)}
                  className="inline-flex items-center px-5 py-2.5 border rounded-xl font-bold transition-all hover:opacity-80"
                  style={{ backgroundColor: theme.colors.surface, borderColor: theme.colors.border, color: theme.colors.text }}
                >
                  <PlusCircle size={18} className="mr-2" />
                  Add Item
                </button>
                <Link
                  to={ROUTES.LISTINGS}
                  className="inline-flex items-center px-6 py-2.5 rounded-xl font-bold transition-all hover:scale-[1.02]"
                  style={{ backgroundColor: theme.colors.primary, color: '#fff' }}
                >
                  Manage Items
                </Link>
                {user.sellerPlan === 'Pro' && (
                  <Link
                    to="/ads"
                    className="inline-flex items-center px-5 py-2.5 border rounded-xl font-bold transition-all hover:opacity-80"
                    style={{ backgroundColor: theme.colors.surface, borderColor: theme.colors.border, color: theme.colors.text }}
                  >
                    <Megaphone size={18} className="mr-2" />
                    Manage Ads
                  </Link>
                )}
              </>
            )}
          </div>
        </div>

        {hasStore && dashboardData?.summary.totalSales === 0 && (
          <div className="border px-4 py-3 rounded-xl flex items-center gap-3"
               style={{ backgroundColor: theme.colors.accent + '15', borderColor: theme.colors.accent, color: theme.colors.text }}>
            <TrendingUp size={20} className="text-amber-500" />
            <span className="font-bold">No items sold yet.</span>
            {!hasItems && (
              <Link to={ROUTES.LISTINGS} className="underline font-black ml-auto hover:opacity-70">
                Add your first item
              </Link>
            )}
          </div>
        )}

      {hasStore ? (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {/* Summary Stats */}
          <div className="p-6 rounded-2xl border shadow-sm transition-all hover:shadow-md" 
               style={{ backgroundColor: theme.colors.surface, borderColor: theme.colors.border }}>
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-xl" style={{ backgroundColor: theme.colors.primary + '15' }}>
                <DollarSign size={24} style={{ color: theme.colors.primary }} />
              </div>
              <span className="text-xs font-bold uppercase tracking-wider" style={{ color: theme.colors.mutedText }}>Total Sales</span>
            </div>
            <div className="text-3xl font-black" style={{ color: theme.colors.text }}>
              ${dashboardData?.summary.totalSales.toFixed(2) || '0.00'}
            </div>
          </div>

          <div className="p-6 rounded-2xl border shadow-sm transition-all hover:shadow-md" 
               style={{ backgroundColor: theme.colors.surface, borderColor: theme.colors.border }}>
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-xl" style={{ backgroundColor: '#10b98115' }}>
                <ShoppingBag size={24} className="text-emerald-500" />
              </div>
              <span className="text-xs font-bold uppercase tracking-wider" style={{ color: theme.colors.mutedText }}>Active Items</span>
            </div>
            <div className="text-3xl font-black" style={{ color: theme.colors.text }}>
              {dashboardData?.summary.activeItems}
            </div>
          </div>

          <div className="p-6 rounded-2xl border shadow-sm transition-all hover:shadow-md" 
               style={{ backgroundColor: theme.colors.surface, borderColor: theme.colors.border }}>
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-xl" style={{ backgroundColor: '#f59e0b15' }}>
                <TrendingUp size={24} className="text-amber-500" />
              </div>
              <span className="text-xs font-bold uppercase tracking-wider" style={{ color: theme.colors.mutedText }}>Estimated Profit</span>
            </div>
            <div className="text-3xl font-black" style={{ color: theme.colors.text }}>
              ${dashboardData?.summary.estimatedProfit.toFixed(2) || '0.00'}
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {dashboardData?.lastSearch ? (
            <div className="rounded-2xl overflow-hidden border" style={{ borderColor: theme.colors.border }}>
              <div className="px-6 py-8" style={{ backgroundColor: theme.colors.primary + '05' }}>
                <h3 className="text-xl font-black flex items-center gap-2 mb-6" style={{ color: theme.colors.text }}>
                  <SearchIcon size={24} className="text-blue-500" />
                  Your Last Search Activity
                </h3>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div className="p-6 rounded-xl border shadow-sm" style={{ backgroundColor: theme.colors.surface, borderColor: theme.colors.border }}>
                    <p className="text-sm font-bold uppercase tracking-wider mb-2" style={{ color: theme.colors.mutedText }}>Query</p>
                    <p className="text-2xl font-black" style={{ color: theme.colors.primary }}>"{dashboardData.lastSearch.query}"</p>
                  </div>
                  <div className="p-6 rounded-xl border shadow-sm" style={{ backgroundColor: theme.colors.surface, borderColor: theme.colors.border }}>
                    <p className="text-sm font-bold uppercase tracking-wider mb-2" style={{ color: theme.colors.mutedText }}>Items Found</p>
                    <p className="text-2xl font-black" style={{ color: theme.colors.primary }}>{dashboardData.lastSearch.count}</p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-3xl overflow-hidden shadow-2xl relative" style={{ backgroundColor: theme.colors.primary }}>
              <div className="absolute top-0 right-0 p-8 opacity-10">
                <ShoppingBag size={200} />
              </div>
              <div className="px-8 py-12 md:px-16 md:py-20 relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
                <div className="text-center md:text-left text-white max-w-lg">
                  <h2 className="text-4xl md:text-5xl font-black leading-tight">
                    Ready to start earning?
                  </h2>
                  <p className="mt-6 text-xl opacity-90">
                    Turn your unused items into cash. Join hundreds of local sellers on Hometown Market today.
                  </p>
                </div>
                <div>
                  <Link
                    to="/plans"
                    className="inline-flex items-center justify-center px-10 py-4 rounded-2xl font-black text-lg transition-all hover:scale-105 shadow-xl"
                    style={{ backgroundColor: '#fff', color: theme.colors.primary }}
                  >
                    Open Your Store
                    <ArrowRight size={22} className="ml-2" />
                  </Link>
                </div>
              </div>
            </div>
          )}

          <div className="p-8 rounded-2xl border shadow-sm text-center max-w-2xl mx-auto" 
               style={{ backgroundColor: theme.colors.surface, borderColor: theme.colors.border }}>
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: theme.colors.primary + '15' }}>
              <ShoppingBag size={32} style={{ color: theme.colors.primary }} />
            </div>
            <h3 className="text-xl font-bold mb-2" style={{ color: theme.colors.text }}>You don't have any items for sale yet.</h3>
            <p className="mb-8" style={{ color: theme.colors.mutedText }}>Start selling today by opening your own store and listing your first item!</p>
            <Link
              to="/plans"
              className="inline-flex items-center px-8 py-3 rounded-xl font-black text-white transition-all hover:opacity-90 shadow-lg"
              style={{ backgroundColor: theme.colors.primary }}
            >
              Get Started Now
            </Link>
          </div>
        </div>
      )}

      {/* Items Section */}
      <div className="mt-12">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-black tracking-tight" style={{ color: theme.colors.text }}>
            {hasStore ? 'Your Items' : 'Recommended for You'}
          </h2>
          {hasStore && (
            <Link to={ROUTES.LISTINGS} className="text-sm font-bold flex items-center gap-1 transition-opacity hover:opacity-70" style={{ color: theme.colors.primary }}>
              View all listings <ArrowRight size={16} />
            </Link>
          )}
        </div>
        
        {hasStore && myItems.length === 0 ? (
          <div className="p-12 rounded-2xl border border-dashed text-center" style={{ borderColor: theme.colors.border, backgroundColor: theme.colors.surface }}>
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: theme.colors.primary + '10' }}>
              <PlusCircle size={32} style={{ color: theme.colors.primary }} />
            </div>
            <h3 className="text-xl font-bold mb-2" style={{ color: theme.colors.text }}>No items listed yet</h3>
            <p className="mb-6" style={{ color: theme.colors.mutedText }}>Your store is open! Now it's time to list your first item.</p>
            <Link
              to={ROUTES.LISTINGS}
              className="inline-flex items-center px-6 py-2.5 rounded-xl font-bold text-white transition-all hover:opacity-90 shadow-md"
              style={{ backgroundColor: theme.colors.primary }}
            >
              Add Your First Item
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {(hasStore ? myItems : searchResults).slice(0, 8).map((item) => (
              <Link key={item.id} to={`/item/${item.id}`} className="group relative border rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1" 
                    style={{ backgroundColor: theme.colors.surface, borderColor: theme.colors.border }}>
                <div className="aspect-square relative overflow-hidden bg-gray-100">
                  {item.images && item.images[0] ? (
                    <img src={item.images[0]} alt={item.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full gap-2" style={{ color: theme.colors.mutedText }}>
                      <ImageIcon size={32} opacity={0.3} />
                      <span className="text-xs font-bold uppercase tracking-tighter opacity-30">No Image</span>
                    </div>
                  )}
                  <div className="absolute top-3 left-3">
                    <span className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider shadow-sm ${
                      item.status.toLowerCase() === 'active' || item.status.toLowerCase() === 'approved' ? 'bg-emerald-500 text-white' : 'bg-amber-500 text-white'
                    }`}>
                      {item.status}
                    </span>
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex justify-between items-start mb-1">
                    <h3 className="font-bold text-sm line-clamp-1 flex-grow" style={{ color: theme.colors.text }}>
                      {item.title}
                    </h3>
                    <p className="font-black text-sm ml-2" style={{ color: theme.colors.primary }}>${item.price}</p>
                  </div>
                  <p className="text-xs line-clamp-2 mb-3 h-8" style={{ color: theme.colors.mutedText }}>{item.description}</p>
                  <div className="flex items-center justify-between pt-3 border-t" style={{ borderColor: theme.colors.border }}>
                    <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: theme.colors.mutedText }}>
                      {new Date(item.createdAt).toLocaleDateString()}
                    </span>
                    <div className="flex gap-2">
                      <button className="p-1.5 rounded-lg border hover:bg-gray-50 transition-colors" style={{ borderColor: theme.colors.border }}>
                        <Settings size={14} style={{ color: theme.colors.mutedText }} />
                      </button>
                      <button className="p-1.5 rounded-lg border hover:bg-gray-50 transition-colors" style={{ borderColor: theme.colors.border }}>
                        <MessageSquare size={14} style={{ color: theme.colors.mutedText }} />
                      </button>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Add Item Modal */}
      {showAddItem && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowAddItem(false)}></div>
          <div className="relative w-full max-w-lg rounded-3xl overflow-hidden shadow-2xl animate-in zoom-in duration-300" 
               style={{ backgroundColor: theme.colors.surface }}>
            <div className="px-8 py-6 border-b flex justify-between items-center" style={{ borderColor: theme.colors.border }}>
              <h3 className="text-xl font-black" style={{ color: theme.colors.text }}>Add New Listing</h3>
              <button onClick={() => setShowAddItem(false)} className="text-2xl font-bold leading-none hover:opacity-50">&times;</button>
            </div>
            <form onSubmit={handleAddItem} className="p-8 space-y-5">
              <div className="space-y-1.5">
                <label className="text-xs font-black uppercase tracking-wider ml-1" style={{ color: theme.colors.mutedText }}>Title</label>
                <input
                  type="text"
                  required
                  value={newItem.title}
                  placeholder="What are you selling?"
                  onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-4 transition-all"
                  style={{ backgroundColor: theme.colors.background, borderColor: theme.colors.border, color: theme.colors.text }}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-black uppercase tracking-wider ml-1" style={{ color: theme.colors.mutedText }}>Description</label>
                <textarea
                  required
                  rows={3}
                  value={newItem.description}
                  placeholder="Tell buyers about your item..."
                  onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-4 transition-all"
                  style={{ backgroundColor: theme.colors.background, borderColor: theme.colors.border, color: theme.colors.text }}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-black uppercase tracking-wider ml-1" style={{ color: theme.colors.mutedText }}>Price ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={newItem.price}
                    placeholder="0.00"
                    onChange={(e) => setNewItem({ ...newItem, price: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-4 transition-all"
                    style={{ backgroundColor: theme.colors.background, borderColor: theme.colors.border, color: theme.colors.text }}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-black uppercase tracking-wider ml-1" style={{ color: theme.colors.mutedText }}>Category</label>
                  <select
                    required
                    value={newItem.categoryId}
                    onChange={(e) => setNewItem({ ...newItem, categoryId: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-4 transition-all appearance-none"
                    style={{ backgroundColor: theme.colors.background, borderColor: theme.colors.border, color: theme.colors.text }}
                  >
                    <option value="">Select Category</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="space-y-1.5 pb-2">
                <label className="text-xs font-black uppercase tracking-wider ml-1" style={{ color: theme.colors.mutedText }}>Location</label>
                <input
                  type="text"
                  placeholder="City, State"
                  value={newItem.location}
                  onChange={(e) => setNewItem({ ...newItem, location: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-4 transition-all"
                  style={{ backgroundColor: theme.colors.background, borderColor: theme.colors.border, color: theme.colors.text }}
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAddItem(false)}
                  className="flex-1 px-6 py-3.5 rounded-xl font-bold border transition-all hover:bg-gray-50"
                  style={{ borderColor: theme.colors.border, color: theme.colors.text }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3.5 rounded-xl font-black text-white transition-all hover:opacity-90 shadow-lg shadow-blue-500/20"
                  style={{ backgroundColor: theme.colors.primary }}
                >
                  Publish Item
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
